import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { PredictionLabel } from '@modules/common/components/PredictionLabel';
import { Category } from '@modules/common/types';
import { useUpdateEditedPredictions } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import {
  getEditPrediction,
  trackFeedbackOnFeedbackV2Events,
} from '@modules/feedback/utils';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { UUID } from 'v2/core/entities/uuid';

interface RemovePredictionModalContentProps {
  label: ReasonOrEntityLabelType;
  feedbackID: UUID;
  category: Category;
  title?: string;
}

export const RemovePredictionModalContent = ({
  label,
  feedbackID,
  category,
  title = 'Remove Prediction',
}: RemovePredictionModalContentProps) => {
  const { closeModal } = useMuiModalContext();
  const { showSuccessNotification } = useNotificationContext();
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const [shouldUpdatePredictions, setShouldUpdatePredictions] = useState(false);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );

  const { isLoading, updateEditedPredictions } =
    useUpdateEditedPredictions(feedbackID);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const isLabelDeleteable =
      label.type === FeedbackLabelType.Reason ||
      label.type === FeedbackLabelType.Entity;

    if (!isLabelDeleteable) {
      return;
    }

    const editPrediction = getEditPrediction({ label, category });

    dispatch({
      type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
      payload: {
        id: feedbackID,
        editPredictions: [
          ...(recordInEditMode?.editPredictions ?? []),
          editPrediction,
        ],
      },
    });
    setShouldUpdatePredictions(true);
  };

  const onAfterEditedPredictions = useCallback(() => {
    trackFeedbackOnFeedbackV2Events({
      eventName: 'Prediction Removed',
      feedbackID: feedbackID,
      toggleLocation: 'Feedback Detail View',
      predictionType: label?.type === 'REASON' ? 'Reason' : 'Tracked Keyword',
      predictionName: label?.displayName,
    });
    showSuccessNotification({
      contentComponent: () => (
        <Stack spacing={1} mt={-0.5}>
          <Typography variant="subtitle1">Thanks for the edit!</Typography>
          <Typography variant="body2">
            We'll use it to level up Enterpret's analytics. This change may take
            up to15 hours to reflect on your organization's Taxonomy.
          </Typography>
        </Stack>
      ),
      props: {},
    });
  }, [feedbackID, label?.displayName, label?.type, showSuccessNotification]);

  useEffect(() => {
    const updatePredictions = async () => {
      setShouldUpdatePredictions(false);
      await updateEditedPredictions({
        onAfterEditedPredictions,
      });
      closeModal();
    };

    if (shouldUpdatePredictions) {
      updatePredictions();
    }
  }, [
    closeModal,
    onAfterEditedPredictions,
    shouldUpdatePredictions,
    updateEditedPredictions,
  ]);

  const onCancel = () => {
    dispatch({
      type: FeedbackOnFeedbackActionType.RESET_RECORD_IN_EDIT_MODE,
      payload: {},
    });
    closeModal();
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <ModalHeader title={title} />
      <Divider />
      <DialogContent>
        <Stack spacing={1}>
          <Typography>
            Are you sure you want to remove the following prediction from this
            record?
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box>
              <CategoryIcon category={category} />
            </Box>
            <PredictionLabel label={label} />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ m: 1, mr: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          type="button"
          endIcon={<EscHotKeyIcon />}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          loading={isLoading}
          color="error"
        >
          Remove
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};
